// The story video's time to a place on the GPS route (AdminVideosPage's Street View panel). Pure functions, no React.
// summary (story_<id>_9x16.json): plan.segments[] (else beats[]) with t: [t0, t1] video seconds and s: [s0, s1]
// course metres. route.json: [[lat, lon], ...]. The course metres run along the map-matched road course
// (summary.roadLength), a little longer than the GPS route, so they are mapped onto it: the start gate to 0 and the
// finish gate to the route's end (summary.gates), else scaled by routeLength / roadLength, else taken as they are.
const EARTH_M = 6371008.8;
const rad = (d) => (d * Math.PI) / 180;

export function haversineM(a, b) {
  const p1 = rad(a[0]); const p2 = rad(b[0]);
  const h = Math.sin((p2 - p1) / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(rad(b[1] - a[1]) / 2) ** 2;
  return 2 * EARTH_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

// Degrees clockwise from north, 0..360.
export function bearingDeg(a, b) {
  const p1 = rad(a[0]); const p2 = rad(b[0]); const dl = rad(b[1] - a[1]);
  const y = Math.sin(dl) * Math.cos(p2);
  const x = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

// The route with its cumulative length (metres) at each point.
export function prepareRoute(points) {
  const pts = (Array.isArray(points) ? points : []).filter((p) => Array.isArray(p) && Number.isFinite(p[0]) && Number.isFinite(p[1]));
  const cum = [0];
  for (let i = 1; i < pts.length; i += 1) cum.push(cum[i - 1] + haversineM(pts[i - 1], pts[i]));
  return { pts, cum, length: cum[cum.length - 1] || 0 };
}

// [lat, lon] at s metres along the route (clamped to the route).
export function pointAt(route, s) {
  const { pts, cum, length } = route;
  if (!pts.length) return null;
  const x = Math.max(0, Math.min(length, s));
  let lo = 0; let hi = cum.length - 1;
  while (hi - lo > 1) { const mid = (lo + hi) >> 1; if (cum[mid] <= x) lo = mid; else hi = mid; }
  const seg = cum[hi] - cum[lo];
  const f = seg > 0 ? (x - cum[lo]) / seg : 0;
  return [pts[lo][0] + (pts[hi][0] - pts[lo][0]) * f, pts[lo][1] + (pts[hi][1] - pts[lo][1]) * f];
}

// The route's direction around s (over s - span .. s + span, the window kept inside the route).
export function headingAt(route, s, span = 10) {
  let a = Math.max(0, s - span); let b = Math.min(route.length, s + span);
  if (b - a < 2 * span) { if (a === 0) b = Math.min(route.length, 2 * span); else a = Math.max(0, route.length - 2 * span); }
  const p = pointAt(route, a); const q = pointAt(route, b);
  return p && q && (p[0] !== q[0] || p[1] !== q[1]) ? bearingDeg(p, q) : 0;
}

// The video's timeline: [{kind, t0, t1, s0, s1}] by time, plus the course-to-route mapping (routeS = (s - offset) * scale).
export function storyTimeline(summary, routeLength) {
  const plan = (summary && summary.plan) || {};
  const raw = Array.isArray(plan.segments) && plan.segments.length ? plan.segments : (summary && Array.isArray(summary.beats) ? summary.beats : []);
  const segs = raw
    .filter((g) => g && Array.isArray(g.t) && Array.isArray(g.s) && g.t.length === 2 && g.s.length === 2 && g.t.every(Number.isFinite) && g.s.every(Number.isFinite))
    .map((g) => ({ kind: g.kind || 'segment', name: g.name || null, t0: g.t[0], t1: g.t[1], s0: g.s[0], s1: g.s[1] }))
    .sort((a, b) => a.t0 - b.t0);
  const road = summary && Number(summary.roadLength);
  const gates = (summary && summary.gates) || {};
  let offset = 0; let scale = 1;
  if (Number.isFinite(gates.start) && Number.isFinite(gates.finish) && gates.finish > gates.start && routeLength > 0) {
    offset = gates.start; scale = routeLength / (gates.finish - gates.start);
  } else if (road > 0 && routeLength > 0) scale = routeLength / road;
  return { segs, offset, scale, courseLength: road > 0 ? road : null };
}

// Course metre at video time t, and the segment it falls in. A hold stays on one spot (its middle), so the
// zoom-ins keep their place; glides and cruises run linearly from s0 to s1.
export function courseMetreAt(timeline, t) {
  const { segs } = timeline;
  if (!segs.length) return null;
  let i = segs.findIndex((g, k) => t >= g.t0 && (t < g.t1 || (k === segs.length - 1 && t <= g.t1)));
  if (i < 0) i = t < segs[0].t0 ? 0 : segs.length - 1;
  const g = segs[i];
  let s;
  if (g.kind === 'hold') s = (g.s0 + g.s1) / 2;
  else if (t <= g.t0) s = g.s0;
  else if (t >= g.t1) s = g.s1;
  else s = g.s0 + ((g.s1 - g.s0) * (t - g.t0)) / (g.t1 - g.t0 || 1);
  return { s, kind: g.kind, name: g.name, index: i };
}

// Everything the panel needs at video time t: course metre, route metre, lat/lon, heading.
export function positionAt(timeline, route, t) {
  const m = courseMetreAt(timeline, t);
  if (!m || !route.pts.length) return null;
  const routeS = Math.max(0, Math.min(route.length, (m.s - timeline.offset) * timeline.scale));
  const p = pointAt(route, routeS);
  return { ...m, routeS, lat: p[0], lon: p[1], heading: headingAt(route, routeS) };
}
